import { ProjectStatus as STATUS } from 'types/project-details.types'
import { Control, useWatch, FieldErrors } from 'react-hook-form'
import { ProjectDetailsFormValues } from 'types/project-details.types'
import { useUserRolesSelector } from 'utils/redux-common-selectors'
import { PROPERTY_STATUS, PROJECT_STATUS } from 'features/common/status'
import { isValidDate } from 'utils/date-time-utils'
import moment from 'moment'
import { InvoiceStatusValues, InvoicingType } from 'types/invoice.types'
// import { ADV_PERMISSIONS } from 'api/access-control'
import type { SelectOption } from 'types/transaction.type'
import { OverideTicketStatusOptions } from './order-intake-form-edit'
import { useMemo } from 'react'

export const useFieldsDisabled = (control: Control<ProjectDetailsFormValues>, projectData?: any) => {
  const status = useWatch({ name: 'status', control })
  const isProjectStatusInvoiced = projectData?.projectStatus === PROJECT_STATUS.invoiced.label
  const invoiceBackDate = useWatch({ name: 'invoiceBackDate', control })
  const remainingPayment = useWatch({ name: 'remainingPayment', control })
  // const { permissions } = useRoleBasedPermissions()
  const { isAdmin } = useUserRolesSelector()

  const projectStatus = status?.value

  const isAllTimeDisabled = true
  const isStatusNew = projectStatus === STATUS.New
  const isStatusActive = projectStatus === STATUS.Active
  const isStatusPunch = projectStatus === STATUS.Punch
  const isStatusClosed = projectStatus === STATUS.Closed
  const isStatusClientPaid = projectStatus === STATUS.ClientPaid
  const isStatusOverPayment = projectStatus === STATUS.Overpayment
  const isStatusInvoiced = projectStatus === STATUS.Invoiced
  const isStatusPaid = projectStatus === STATUS.Paid
  // const isStatusPastDue = projectStatus === STATUS.PastDue
  const isStatusCancelled = projectStatus === STATUS.Cancelled
  const isStatusAwaitingPunch = projectStatus === STATUS.Awaiting_Punch
  const isStatusReconciled = projectStatus === STATUS.Reconcile

  return {
    isStatusNew,
    isStatusActive,
    isStatusPunch,
    isStatusInvoiced,
    isStatusClientPaid,
    isStatusClosed,
    isStatusCancelled,
    isStatusReconciled,
    // Project Management form fields states
    isWOAStartDateRequired: isStatusActive,
    isWOACompletionDateRequired: isStatusClosed,
    isClientWalkthroughDateRequired: isStatusClosed,
    isClientSignOffDateRequired: isStatusClosed,

    isWOAStartDisabled:
      // !permissions.some(p => ['PROJECTDETAIL.MGMT.WOASTART.EDIT', 'ALL'].includes(p)) ||
      isStatusClosed || isStatusInvoiced || isStatusClientPaid || isStatusPaid || isStatusOverPayment,
    isWOACompletionDisabled:
      isStatusClosed ||
      isStatusNew ||
      isStatusActive ||
      isStatusInvoiced ||
      (isStatusClientPaid && !isAdmin) ||
      isStatusPaid ||
      isStatusOverPayment,
    isClientStartDateDisabled:
      // !permissions.some(p => ['PROJECTDETAIL.MGMT.CLIENTSTART.EDIT', 'ALL'].includes(p)) ||
      isStatusClosed || isStatusInvoiced || isStatusClientPaid || isStatusPaid || isStatusOverPayment,
    isClientDueDateDisabled:
      // !permissions.some(p => ['PROJECTDETAIL.MGMT.CLIENTDUEDATE.EDIT', 'ALL'].includes(p)) ||
      isStatusClosed || isStatusInvoiced || isStatusClientPaid || isStatusPaid || isStatusOverPayment,
    isClientWalkthroughDisabled:
      isStatusNew ||
      isStatusActive ||
      isStatusAwaitingPunch ||
      isStatusInvoiced ||
      (isStatusClientPaid && !isAdmin) ||
      isStatusPaid ||
      isStatusOverPayment,
    isClientSignOffDisabled:
      isStatusNew ||
      isStatusActive ||
      isStatusAwaitingPunch ||
      (isStatusClientPaid && !isAdmin) ||
      isStatusPaid ||
      isStatusOverPayment ||
      isStatusInvoiced,
    // isReconcileDisabled: !permissions.some(p => ['PROJECTDETAIL.MGMT.PROJECTVERIFIED.EDIT', 'ALL'].includes(p)),

    // Invoicing and payment form fields states
    isOriginalSOWAmountDisabled: isAllTimeDisabled,
    isFinalSOWAmountDisabled: isAllTimeDisabled,
    isInvoiceNumberDisabled: isStatusPaid,
    isUploadAttachmentDisabled: isStatusPaid,
    isInvoiceBackDateDisabled: isStatusPaid,
    isPaymentTermsDisabled: !isStatusInvoiced && !invoiceBackDate,
    isWOAInvoiceDateDisabled: isAllTimeDisabled,
    isOverPaymentDisalbed: isAllTimeDisabled,
    isWOAExpectedPayDateDisabled: isAllTimeDisabled,
    isRemainingPaymentDisabled: isAllTimeDisabled,
    isPaymentDisabled: !(isStatusClientPaid || isProjectStatusInvoiced || invoiceBackDate) || remainingPayment === 0,

    // Contacts field states
    // isProjectCoordinatorDisabled: !permissions.some(p => ['PROJECTDETAIL.CONTACT.PC.EDIT', 'ALL'].includes(p)),
    isProjectCoordinatorPhoneNumberDisabled: isAllTimeDisabled,
    isProjectCoordinatorExtensionDisabled: isAllTimeDisabled,
    // isFieldProjectManagerDisabled: !permissions.some(p => ['PROJECTDETAIL.CONTACT.FPM.EDIT', 'ALL'].includes(p)),
    isFieldProjectManagerPhoneNumberDisabled: isAllTimeDisabled,
    isFieldProjectManagerExtensionDisabled: isAllTimeDisabled,
    // isClientDisabled: !permissions.some(p => ['PROJECTDETAIL.CONTACT.CLIENT.EDIT', 'ALL'].includes(p)),

    // Location Form fields states
    // isAddressDisabled: !permissions.some(p => ['PROJECTDETAIL.CONTACT.ADDRESS.EDIT', 'ALL'].includes(p)),

    // isCityDisabled: !permissions.some(p => ['PROJECTDETAIL.CONTACT.ADDRESS.EDIT', 'ALL'].includes(p)),
    // isStateDisabled: !permissions.some(p => ['PROJECTDETAIL.CONTACT.ADDRESS.EDIT', 'ALL'].includes(p)),
    // isZipDisabled: !permissions.some(p => ['PROJECTDETAIL.CONTACT.ADDRESS.EDIT', 'ALL'].includes(p)),
    // isMarketDisabled: !permissions.some(p => ['PROJECTDETAIL.CONTACT.MARKET.EDIT', 'ALL'].includes(p)),
    // isGateCodeDisabled: !permissions.some(p => ['PROJECTDETAIL.CONTACT.GATECODE.EDIT', 'ALL'].includes(p)),
    // isLockBoxCodeDisabled: !permissions.some(p => ['PROJECTDETAIL.CONTACT.LOCKBOX.EDIT', 'ALL'].includes(p)),
  }
}

export const useFieldsRequired = (control: Control<ProjectDetailsFormValues>) => {
  const status = useWatch({ name: 'status', control })

  const projectStatus = status?.value
  const isStatusActive = projectStatus === STATUS.Active
  const isStatusClosed = projectStatus === STATUS.Closed
  const isStatusAwaitingPunch = projectStatus === STATUS.Awaiting_Punch
  const isStatusPunch = projectStatus === STATUS.Punch
  const isStatusReconcile = projectStatus === STATUS.Reconcile

  return {
    // Project Management form fields states
    isWOAStartDateRequired: isStatusActive,
    isWOACompletionDateRequired: isStatusClosed || isStatusPunch || isStatusReconcile,
    isWOACompletionDateRequiredNew: isStatusClosed || isStatusAwaitingPunch || isStatusReconcile,
    isClientWalkthroughDateRequired: isStatusClosed,
    isClientWalkthroughDateRequiredNew: isStatusPunch,
    isClientSignOffDateRequired: isStatusClosed,
  }
}

/**
 * @description - useSubFormErrors returns object of booleans indicating form errors.
 * @param errors - errors object of react hook form errors of all fields
 * @returns - isInvoiceAndPaymentFormErrors for showing error indicator on Invoice & Payment form tab
 * isProjectDetailsFormErrors for showing error indicator on Project Management form tab
 */
export const useSubFormErrors = (errors: FieldErrors<ProjectDetailsFormValues>) => {
  return {
    isInvoiceAndPaymentFormErrors:
      !!errors.invoiceAttachment?.message || !!errors.paymentTerms || !!errors.invoiceBackDate,
    isProjectManagementFormErrors:
      !!errors.woaCompletionDate ||
      !!errors.clientWalkthroughDate ||
      !!errors.clientSignOffDate ||
      !!errors.type ||
      !!errors.woaStartDate,
    isContactsFormErrors:
      !!errors.projectCoordinator || !!errors.fieldProjectManager || !!errors.client || !!errors.clientType,
    isLocationFormErrors: !!errors.zip || !!errors.city || !!errors.market || !!errors.address || !!errors.state,
  }
}

// Min of woa start date will be client start date
// Min of woa completion date will be woa start date
export const useMinMaxDateSelector = (control: Control<ProjectDetailsFormValues>) => {
  const clientStartDate = useWatch({ name: 'clientStartDate', control }) ?? new Date().toString()
  const woaStartDate = useWatch({ name: 'woaStartDate', control }) ?? new Date().toString()

  return {
    woaStartMin: isValidDate(new Date(clientStartDate))
      ? new Date(clientStartDate)?.toISOString()?.split('T')?.[0]
      : '',
    woaCompletionMin: isValidDate(new Date(woaStartDate)) ? moment.utc(woaStartDate).format('YYYY-MM-DD') : '',
  }
}

// Current date
export const useCurrentDate = () => {
  let date = new Date().toISOString().split('T')[0]
  return date
}

export const useInvoiceFormEnabledFields = (control: Control<InvoicingType>, invoice, invoiced) => {
  const { isAdmin, isAccounting } = useUserRolesSelector()
  const isAccAndAdmin = isAdmin || isAccounting
  const watchPaymentValue = useWatch({ name: 'payment', control })
  const watchInvoiceNumber = useWatch({ name: 'invoiceNumber', control })
  const watchStatus = useWatch({ name: 'status', control }) as SelectOption
  const isInvoiceEmpty = watchInvoiceNumber?.trim() === ''
  const payment = parseFloat(watchPaymentValue!?.toString())
  // const { permissions } = useRoleBasedPermissions()
  // const isAdmin = permissions?.includes('ALL')
  // const canCreateInvoice = permissions.some(p => [ADV_PERMISSIONS.invoiceEdit, 'ALL'].includes(p))
  // const isInvoicedDateEnabled = permissions.some(p => [ADV_PERMISSIONS.invoiceDateEdit, 'ALL'].includes(p))
  // const allowZeroAmountInvoice = permissions.some(p => [ADV_PERMISSIONS.allowZeroAmountInvoice, 'ALL'].includes(p))
  const isCancelled = invoice?.status === InvoiceStatusValues.cancelled
  const isPaid = invoice?.status === InvoiceStatusValues.paid
  const cancelledSelected = watchStatus?.value === InvoiceStatusValues.cancelled

  return {
    isPaymentReceivedDisabled: isNaN(payment) || payment <= 0 || isPaid || isCancelled,
    addRowDisabled: isPaid || isCancelled,
    deleteRowDisabled: isPaid || isCancelled,
    invoiceNumberDisabled: isPaid || isCancelled || !isAccAndAdmin,
    invoiceDateDisabled: isPaid || isCancelled || !isAccAndAdmin,
    paymentTermDisabled: isPaid || isCancelled || !isAccAndAdmin,
    paymentDisabled: isPaid || isCancelled || cancelledSelected || !isAccAndAdmin,
    invoiceStatusDisabled: isCancelled || !isAccAndAdmin,
    isSaveDisabled: !invoiced || isCancelled || isInvoiceEmpty,
  }
}

// export const usePropertyStatusSelectOptions = () => {
//   // if (!project) return []
//   const { isAdmin, isAccounting } = useUserRolesSelector()

//   const isAdminOrisAccounting = isAccounting || isAdmin

//   // In case of other users than admin or accounting, the first option of OverideTicketStatusOptions is the
//   // DISPUTED which should be hide in this case
//   const overideStatusOptions = !isAdminOrisAccounting ? OverideTicketStatusOptions.slice(1) : OverideTicketStatusOptions

//   return overideStatusOptions
// }

export const useProjectOverrideStatusSelectOptions = projectData => {
  let overrideProjectStatusOptions
  const projectStatusId = projectData?.projectStatusId
  const previousProjectStatus = projectData?.previousStatus
  const isRevised = projectData?.isRevised
  const isDeclined = projectData?.isDeclined

  return useMemo(() => {
    if (!projectStatusId) return []
    // Setting Override Status dropdown on the basis of Project Status
    if (projectStatusId !== undefined) {
      overrideProjectStatusOptions = OverideTicketStatusOptions

      //filter the disputed in case of having status of draft, contact1 or contact2
      if (
        projectStatusId === Number(PROPERTY_STATUS.contacttwo.value) ||
        projectStatusId === Number(PROPERTY_STATUS.draft.value) ||
        projectStatusId === Number(PROPERTY_STATUS.contactone.value)
      ) {
        overrideProjectStatusOptions = OverideTicketStatusOptions.filter(
          d => d.value !== Number(PROPERTY_STATUS.disputed.value),
        )
      }

      // if (projectStatusId === Number(PROPERTY_STATUS.revision.value)) {
      //   overrideProjectStatusOptions = [
      //     PROPERTY_STATUS.new,
      //     PROPERTY_STATUS.scheduled,
      //     PROPERTY_STATUS.estimated,
      //     PROPERTY_STATUS.submitted,
      //     PROPERTY_STATUS.disputed,
      //     PROPERTY_STATUS.cancelled,
      //   ]

      //   if (isRevised) {
      //     overrideProjectStatusOptions = overrideProjectStatusOptions.concat([PROPERTY_STATUS.revision])
      //   }
      //   if (isDeclined) {
      //     overrideProjectStatusOptions = overrideProjectStatusOptions.concat([PROPERTY_STATUS.declined])
      //   }
      // }

      // Project Status -> Disputed
      // In case of disputed, all the cases will be implemented on the basis of previous Status
      if (projectStatusId === Number(PROPERTY_STATUS.disputed.value)) {
        // Last Project Status -> New
        if (previousProjectStatus === Number(PROPERTY_STATUS.new.value)) {
          overrideProjectStatusOptions = [PROPERTY_STATUS.new, PROPERTY_STATUS.disputed, PROPERTY_STATUS.cancelled]
        }

        // Last Project Status -> Schedule
        if (previousProjectStatus === Number(PROPERTY_STATUS.scheduled.value)) {
          overrideProjectStatusOptions = [
            PROPERTY_STATUS.new,
            PROPERTY_STATUS.scheduled,
            PROPERTY_STATUS.disputed,
            PROPERTY_STATUS.cancelled,
          ]
        }

        // Last Project Status -> estimated
        else if (previousProjectStatus === Number(PROPERTY_STATUS.estimated.value)) {
          overrideProjectStatusOptions = [
            PROPERTY_STATUS.new,
            PROPERTY_STATUS.scheduled,
            PROPERTY_STATUS.estimated,
            PROPERTY_STATUS.disputed,
            PROPERTY_STATUS.cancelled,
          ]
        }

        // Last Project Status -> submitted
        else if (previousProjectStatus === Number(PROPERTY_STATUS.submitted.value)) {
          overrideProjectStatusOptions = [
            PROPERTY_STATUS.new,
            PROPERTY_STATUS.scheduled,
            PROPERTY_STATUS.estimated,
            PROPERTY_STATUS.submitted,
            PROPERTY_STATUS.disputed,
            PROPERTY_STATUS.cancelled,
          ]

          if (isRevised) {
            overrideProjectStatusOptions = overrideProjectStatusOptions.concat([PROPERTY_STATUS.revision])
          }
          if (isDeclined) {
            overrideProjectStatusOptions = overrideProjectStatusOptions.concat([PROPERTY_STATUS.declined])
          }
        } else if (previousProjectStatus === Number(PROPERTY_STATUS.approved.value)) {
          overrideProjectStatusOptions = [
            PROPERTY_STATUS.new,
            PROPERTY_STATUS.scheduled,
            PROPERTY_STATUS.estimated,
            PROPERTY_STATUS.submitted,
            PROPERTY_STATUS.approved,
            PROPERTY_STATUS.disputed,
            PROPERTY_STATUS.cancelled,
          ]
          if (isRevised) {
            overrideProjectStatusOptions = overrideProjectStatusOptions.concat([PROPERTY_STATUS.revision])
          }
          if (isDeclined) {
            overrideProjectStatusOptions = overrideProjectStatusOptions.concat([PROPERTY_STATUS.declined])
          }
        }

        // Last Project Status -> closed
        else if (previousProjectStatus === Number(PROPERTY_STATUS.closed.value)) {
          overrideProjectStatusOptions = [
            PROPERTY_STATUS.new,
            PROPERTY_STATUS.scheduled,
            PROPERTY_STATUS.estimated,
            PROPERTY_STATUS.submitted,
            PROPERTY_STATUS.approved,
            PROPERTY_STATUS.closed,
            PROPERTY_STATUS.disputed,
            PROPERTY_STATUS.cancelled,
          ]
        }

        // Last Project Status -> Invoiced
        else if (previousProjectStatus === Number(PROPERTY_STATUS.invoiced.value)) {
          overrideProjectStatusOptions = [
            PROPERTY_STATUS.new,
            PROPERTY_STATUS.scheduled,
            PROPERTY_STATUS.estimated,
            PROPERTY_STATUS.submitted,
            PROPERTY_STATUS.approved,
            PROPERTY_STATUS.closed,
            PROPERTY_STATUS.invoiced,
            PROPERTY_STATUS.disputed,
            PROPERTY_STATUS.cancelled,
          ]
        }

        // Last Project Status -> Client paid
        else if (previousProjectStatus === Number(PROPERTY_STATUS.clientPaid.value)) {
          overrideProjectStatusOptions = [
            PROPERTY_STATUS.new,
            PROPERTY_STATUS.scheduled,
            PROPERTY_STATUS.estimated,
            PROPERTY_STATUS.submitted,
            PROPERTY_STATUS.approved,
            PROPERTY_STATUS.closed,
            PROPERTY_STATUS.invoiced,
            PROPERTY_STATUS.clientPaid,
            PROPERTY_STATUS.disputed,
            PROPERTY_STATUS.cancelled,
          ]
        }

        // Last Project Status -> Paid
        else if (previousProjectStatus === Number(PROPERTY_STATUS.paid.value)) {
          overrideProjectStatusOptions = [
            PROPERTY_STATUS.new,
            PROPERTY_STATUS.scheduled,
            PROPERTY_STATUS.estimated,
            PROPERTY_STATUS.submitted,
            PROPERTY_STATUS.approved,
            PROPERTY_STATUS.closed,
            PROPERTY_STATUS.invoiced,
            PROPERTY_STATUS.clientPaid,
            PROPERTY_STATUS.paid,
            PROPERTY_STATUS.disputed,
            PROPERTY_STATUS.cancelled,
          ]
        } else if (isDeclined && isRevised) {
          overrideProjectStatusOptions = [
            PROPERTY_STATUS.new,
            PROPERTY_STATUS.scheduled,
            PROPERTY_STATUS.estimated,
            PROPERTY_STATUS.submitted,
            PROPERTY_STATUS.revision,
            PROPERTY_STATUS.declined,
            PROPERTY_STATUS.disputed,
            PROPERTY_STATUS.cancelled,
          ]
        }
      }
    }
    return overrideProjectStatusOptions
  }, [projectData, projectStatusId])
}
